import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import logo from "@/assets/galahadji-logo.png";
import { CheckCircle2, Eraser, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    const scale = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * scale;
    c.height = 160 * scale;
    const ctx = c.getContext("2d");
    ctx.scale(scale, scale);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasInk.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current) onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
    onChange("");
  };

  return (
    <div>
      <canvas ref={canvasRef} className="w-full h-[160px] border-2 border-dashed border-gray-300 rounded-md bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-pad" />
      <button type="button" onClick={clear} className="mt-2 text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1" data-testid="signature-clear">
        <Eraser className="w-3.5 h-3.5" /> Clear signature
      </button>
    </div>
  );
}

export default function SignPage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [typed, setTyped] = useState("");
  const [sig, setSig] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/esign/${token}`)
      .then((r) => { setDoc(r.data); setTyped(r.data.signer_name || ""); if (r.data.status === "signed") setDone(true); })
      .catch((e) => setError(e.response?.data?.detail || "This signing link is invalid or has expired."));
  }, [token]);

  const sign = async () => {
    if (!typed.trim()) { setError("Please type your full legal name."); return; }
    if (!agreed) { setError("Please check the agreement box to sign."); return; }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/public/esign/${token}/sign`, { typed_name: typed.trim(), signature_data: sig, agreed: true });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0f1830] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <img src={logo} alt="Galahadji" className="h-14 w-auto object-contain" />
        </div>
        {error && !doc && <div className="bg-white rounded-lg p-10 text-center text-red-600" data-testid="sign-error">{error}</div>}
        {doc && (
          <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
            {done ? (
              <div className="p-12 text-center" data-testid="sign-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
                <h2 className="text-2xl font-bold mt-4" style={{ fontFamily: "Arial" }}>Document signed</h2>
                <p className="text-gray-500 mt-2">Thank you{typed ? `, ${typed}` : ""}. A copy of the signed document is kept on file by Galahadji Acquisitions. You may close this page.</p>
              </div>
            ) : (
              <>
                <div className="bg-[#d4a017] px-6 py-4">
                  <div className="font-bold text-[#0f1830]" style={{ fontFamily: "Arial" }}>Signature requested — {doc.signer_name} ({doc.signer_role})</div>
                  <div className="text-[#0f1830]/80 text-sm">Review the document below, then sign at the bottom.</div>
                </div>
                <div className="p-6 sm:p-10 max-h-[55vh] overflow-y-auto border-b" data-testid="sign-document">
                  <div className="flex items-center gap-4 border-b-4 border-[#d4a017] pb-3 mb-5">
                    <img src={logo} alt="logo" className="h-12 w-auto object-contain" />
                    <div><div className="font-bold" style={{ fontFamily: "Arial" }}>Galahadji Acquisitions Inc</div>
                      <div className="text-xs text-gray-500">Las Vegas, NV</div></div>
                  </div>
                  <div style={{ fontFamily: "Georgia, serif", lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: doc.html }} />
                </div>
                <div className="p-6 sm:p-8 bg-gray-50">
                  <div className="flex items-center gap-2 font-bold" style={{ fontFamily: "Arial" }}><PenLine className="w-4 h-4" /> Sign here</div>
                  <div className="mt-3">
                    <label className="text-xs text-gray-500">Draw your signature (optional)</label>
                    <SignaturePad onChange={setSig} />
                  </div>
                  <div className="mt-4">
                    <label className="text-xs text-gray-500">Type your full legal name <span className="text-red-500">*</span></label>
                    <input value={typed} onChange={(e) => setTyped(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2.5 mt-1" data-testid="sign-typed-name" />
                  </div>
                  <label className="flex items-start gap-2 mt-4 text-sm text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-4 w-4 accent-[#d4a017]" data-testid="sign-agree" />
                    I agree that my electronic signature is the legal equivalent of my handwritten signature on this document.
                  </label>
                  {error && <p className="text-sm text-red-600 mt-3" data-testid="sign-error-inline">{error}</p>}
                  <button onClick={sign} disabled={busy} data-testid="sign-submit"
                    className="mt-5 w-full sm:w-auto bg-[#0f1830] text-white font-semibold px-10 py-3 rounded-md hover:bg-[#0f1830]/90 disabled:opacity-60">
                    {busy ? "Signing…" : "Sign document"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <p className="text-center text-white/40 text-xs mt-6">Secure e-signature powered by Galahadji Acquisition OS</p>
      </div>
    </div>
  );
}
